import { createFileRoute, Link } from "@tanstack/react-router";
import { CursorCandles } from "@/components/CursorCandles";
import { Crosshair } from "@/components/Crosshair";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { API_ENDPOINTS } from "@/lib/types";

export const Route = createFileRoute("/docs")({
  head: () => ({
    meta: [
      { title: "Docs — How Hoodrank scores X accounts" },
      {
        name: "description",
        content:
          "Hoodrank documentation: scoring methodology, follower-quality weighting, API endpoints and a 30-second quick start.",
      },
      { property: "og:title", content: "Hoodrank Docs — Methodology & API" },
      {
        property: "og:description",
        content:
          "How follower-quality scoring works, which Hoodrank API endpoints power it, and how to rank your first handle.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: DocsPage,
});

const TOC = [
  { id: "overview", label: "Overview" },
  { id: "methodology", label: "Methodology" },
  { id: "api", label: "API reference" },
  { id: "quickstart", label: "Quick start" },
];

const WEIGHTS = [
  {
    k: "Influencers",
    v: "High",
    d: "KOLs and operators with proven reach inside crypto. The heaviest single input into a score.",
  },
  {
    k: "Venture capital",
    v: "High",
    d: "Funds and their partners. A fund following you is a costly signal — it is rarely accidental.",
  },
  {
    k: "Projects",
    v: "Medium",
    d: "Protocol and product accounts. Signals ecosystem relevance rather than personal reach.",
  },
  {
    k: "Everyone else",
    v: "Near zero",
    d: "Unclassified and low-signal accounts. Bots and bought followers do not move the number.",
  },
];

function Section({
  id,
  eyebrow,
  title,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 border-t border-line py-12 md:py-16">
      <p className="label mb-5">{eyebrow}</p>
      <h2 className="max-w-[22ch]">{title}</h2>
      <div className="mt-6 space-y-4 text-[16px] text-ink-soft [&_p]:max-w-[68ch]">{children}</div>
    </section>
  );
}

function DocsPage() {
  return (
    <>
      <CursorCandles />
      <Crosshair />
      <SiteHeader />

      <main data-candles className="grid-paper">
        <div className="shell py-14 md:py-20">
          <div className="max-w-none lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:items-start lg:gap-12 xl:grid-cols-[14rem_minmax(0,1fr)] xl:gap-16">
            <aside className="mb-10 lg:sticky lg:top-24 lg:mb-0 lg:self-start">
              <p className="label mb-4">On this page</p>
              <nav
                aria-label="Contents"
                className="flex flex-wrap gap-2 lg:flex-col lg:gap-0 lg:border-l lg:border-line"
              >
                {TOC.map((t) => (
                  <a
                    key={t.id}
                    href={`#${t.id}`}
                    className="mono border border-line px-3 py-1.5 text-[12px] tracking-[0.08em] text-ink-soft uppercase hover:border-ink hover:text-ink lg:border-0 lg:border-l-2 lg:border-transparent lg:px-4 lg:py-2.5 lg:-ml-px lg:hover:border-accent lg:hover:text-ink"
                  >
                    {t.label}
                  </a>
                ))}
              </nav>
            </aside>

            <div className="min-w-0">
              <p className="label mb-6">Documentation</p>
              <h1 className="max-w-[16ch] !text-[clamp(40px,6vw,88px)]">
                The <span className="hollow">method</span>
              </h1>
              <p className="mt-5 max-w-[62ch] text-[17px] text-ink-soft">
                Hoodrank turns an X follower graph into a single reputation number. Here is exactly
                what goes in, what comes out, and where the data comes from.
              </p>

              <Section id="overview" eyebrow="01 · Overview" title="What Hoodrank scores">
                <p>
                  Hoodrank scores X (Twitter) accounts by the quality of their followers, not the
                  quantity. Every handle gets a score in reputation points — unbounded, e.g.{" "}
                  <span className="mono text-ink">1,831</span> — plus a tier: Elite (≥ 1,500),
                  Strong (≥ 600) or Watch.
                </p>
                <p>
                  A score is a read on distribution, not on truth. Ten thousand bots are worth less
                  than one fund partner. Nothing here is financial advice — scores reflect signal.
                </p>
              </Section>

              <Section
                id="methodology"
                eyebrow="02 · Methodology"
                title="How follower quality is scored"
              >
                <p>
                  Every follower is classified, then weighted. The score is the weighted mass of a
                  handle&apos;s classified followers, expressed as reputation points. Higher points
                  mean a denser high-signal audience; there is no fixed maximum.
                </p>
                <div className="mt-8 w-full border border-ink bg-bg-panel">
                  <div className="hidden border-b border-line sm:grid sm:grid-cols-[minmax(0,11rem)_6rem_minmax(0,1fr)] sm:gap-4 sm:px-5 sm:py-3">
                    <span className="label">Class</span>
                    <span className="label">Weight</span>
                    <span className="label">Why it counts</span>
                  </div>
                  {WEIGHTS.map((w, i) => (
                    <div
                      key={w.k}
                      className={`grid grid-cols-1 gap-2 p-5 sm:grid-cols-[minmax(0,11rem)_6rem_minmax(0,1fr)] sm:items-start sm:gap-4 ${
                        i > 0 ? "border-t border-line" : ""
                      }`}
                    >
                      <p className="mono text-[13px] font-semibold text-ink">{w.k}</p>
                      <p className="label !text-accent-deep">{w.v}</p>
                      <p className="min-w-0 text-[14px] leading-relaxed text-ink-soft">{w.d}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-8">
                  Score changes are tracked over 7-day and 30-day windows so a new follow from a
                  high-weight account shows up as a visible delta rather than a silent re-rank.
                </p>
              </Section>

              <Section id="api" eyebrow="03 · API reference" title="Hoodrank API endpoints">
                <p>
                  The follower graph is served by the Hoodrank API. These endpoints power the live
                  UI:
                </p>
                <div className="mt-8 w-full border border-ink bg-bg-panel">
                  <div className="hidden border-b border-line lg:grid lg:grid-cols-[4.5rem_minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-4 lg:px-5 lg:py-3">
                    <span className="label">Method</span>
                    <span className="label">Path</span>
                    <span className="label">Returns</span>
                  </div>
                  {API_ENDPOINTS.map((e, i) => (
                    <div
                      key={e.path}
                      className={`grid grid-cols-1 gap-2 p-5 lg:grid-cols-[4.5rem_minmax(0,1.15fr)_minmax(0,1fr)] lg:items-start lg:gap-4 ${
                        i > 0 ? "border-t border-line" : ""
                      }`}
                    >
                      <span className="w-fit bg-accent px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-[0.08em] text-accent-ink uppercase">
                        {e.method}
                      </span>
                      <p className="mono min-w-0 break-all text-[13px] font-semibold text-ink">
                        {e.path}
                      </p>
                      <p className="min-w-0 text-[14px] leading-relaxed text-ink-soft">
                        {e.description}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-8">
                  Responses are typed in <span className="mono text-ink">src/lib/types.ts</span>.
                  The Express API in <span className="mono text-ink">back/</span> is the only data
                  source for the frontend.
                </p>
              </Section>

              <Section
                id="quickstart"
                eyebrow="04 · Quick start"
                title="Rank a handle in 30 seconds"
              >
                <ol className="grid gap-3 sm:grid-cols-3 sm:gap-4">
                  {[
                    "Open the ranking terminal.",
                    "Paste any @handle or x.com profile URL.",
                    "Read the score, tier and the followers driving it.",
                  ].map((step, i) => (
                    <li key={step} className="border border-line bg-bg-panel p-4">
                      <p className="label !text-accent-deep">{String(i + 1).padStart(2, "0")}</p>
                      <p className="mono mt-3 text-[13px] leading-relaxed text-ink-soft">{step}</p>
                    </li>
                  ))}
                </ol>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/app" className="btn btn-dark w-full sm:w-auto">
                    Check rank →
                  </Link>
                  <Link to="/leaderboard" className="btn btn-outline w-full sm:w-auto">
                    Browse leaderboard
                  </Link>
                </div>
              </Section>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </>
  );
}
